<template>
  <div class="comment-box" :class="{selected,'isEng' : $isStories}">
    <div class="card-container-comment is-flex">
      <div class="card-image-container is-flex">
        <figure class="poster-image"><a @click="view(comment.user)"><img :src="thumbnail(comment.user.avatar)" :alt="comment.user.userName"></a></figure>
      </div>
      <div class="card-image-text is-flex">
        <a @click="view(comment.user)">
          <p class="poster title is-7">{{comment.user.userName}}
            <span><i class="fal" v-bind:class="`fa-${comment.user.badgeImage}`"></i></span>
          </p>
        </a>
        <p :data-balloon="formatTimestamp(this.comment.created)" data-balloon-pos="up" class="timestamp is-flex">
          <span>{{formattedDate}}</span>
        </p>
      </div>
    </div>
    <div class="comment-text">
      <carousel v-if="hasAttachments && !editing" v-bind="{attachments, canCreate, isGrid, canEdit}" @position="updateCarouselPosition" @annotate="makeAnnotation" class="comment-carousel" :activeTab="stateActiveTab" @edit="editAttachment" @deleting="showDeleteAttachmentModal = true" :initialPosition="carouselPosition"></carousel>
      <div class="text-and-like is-flex" :class="{'text-and-like-carousel' : hasAttachments}">
        <add ref="add" v-if="editing" :comment="comment" :update="true" @created="editing = false, saving = false" @saving="saving = $event" :post="post" @cancel="initiateCancel" @delete="showDeleteModal = true"></add>
        <div v-else class="post-text is-flex" :class="{'post-text-yscAuditorium': $isYSC && isYSCAuditorium }">
          <html-view class="text" :content="comment.body" :isComment="true"></html-view>
        </div>
        <div class="button-container is-flex" v-if="!editing">
          <div class="reply-and-like is-flex">
            <span class="reply primary-hover cursor-pointer" :data-balloon="$t('projectPage.reply')" data-balloon-pos="up" v-if="!editing && canCreateComment" @click="$emit('reply', comment)"><i class="fal fa-reply"></i></span>
            <span v-if="!editing" class="thumb primary-hover cursor-pointer" :class="{[uniqueClass]: true}" @click="like" @mouseenter="showLikes" @mouseleave="hideLikes">
              <span class="dropper" v-if="dropdowns.likes.show && !isTouch && comment.metadata.likes > 0">
                <drop-down :users="dropdowns.likes.users" :loading="dropdowns.likes.loading" @fetch="fetchLikedBy" @close="hideLikes" @click="toggleLikes"></drop-down>
              </span>
              <i :class="{'has-text-primary far': hasLiked || this.submitLike, 'fal': !hasLiked || !this.submitLike}" class="fa-thumbs-up"></i>
              <template v-if="comment.metadata.likes">{{comment.metadata.likes}}</template></span>
          </div>
          <div class="edit-or-report is-flex">
            <span v-if="isDownloadable" class="container" :data-balloon="$t('projectPage.download')" data-balloon-pos="up" @click="downloadAttachment(comment.attachments)"><i class="fal fa-download"></i></span>
            <span v-if="isCreator || isAdmin" @click="edit" :class="{active:editing}" class="edit-comment cursor-pointer primary-hover" :data-balloon="$t('projectPage.edit')" data-balloon-pos="up"><i class="fal fa-pencil-alt"></i></span>
            <report class="report-button" :comment="comment" v-if="!canEdit || isAdmin"></report>
            <span v-if="isEditCommentPermission"
                  @created="editing = false, saving = false" @saving="saving = $event" @cancel="initiateCancel"
                  @click="showDeleteModal = true" class="container cursor-pointer" :data-balloon="$t('projectPage.delete')" data-balloon-pos="up">
              <i class="fal fa-trash-alt"></i>
            </span>
          </div>
        </div>
      </div>
    </div>
    <modal-custom
      v-if="showEditModal"
      :hasFooter="true"
      :title="$t('projectPage.confirmCancel')" :text="$t('projectPage.cancelEditReply')"
      :successText="$t('yes')" :cancelText="$t('no')"
      @modalSuccess="confirmCancel" @modalCancel="toggleEditModal"
    ></modal-custom>
    <modal-custom
      v-if="showDeleteModal"
      :hasFooter="true"
      :title="$t('projectPage.confirmDelete')" :text="$t('projectPage.deleteReply')"
      :successText="$t('projectPage.yesDelete')" :cancelText="$t('cancel')"
      @modalSuccess="deleteComment" @modalCancel="showDeleteModal = false"
    ></modal-custom>
    <modal-custom
      v-if="showDeleteAttachmentModal"
      :hasFooter="true"
      :title="$t('projectPage.confirmDeleteAttachment')" :text="$t('projectPage.deleteAttachment')"
      :successText="$t('projectPage.yesDelete')" :cancelText="$t('cancel')"
      @modalSuccess="deleteAttachment" @modalCancel="showDeleteAttachmentModal = false"
    ></modal-custom>
    <modal-progress ref="downloadProgress"></modal-progress>
  </div>
</template>

<script>
import throttle from 'lodash/throttle'
import auth from '@/mixins/auth'
import HtmlView from '@/components/Controls/HTMLView'
import moment from 'moment'
import Carousel from '@/components/Forms/AttachmentsCarousel'
import Add from './Add'
import Report from '@/components/UIReport'
import DropDown from '@/components/Controls/MetaCountsDropdown'
import dropDownMixin from '@/components/Controls/MetaCountsDropdown/mixin'

import state from '../../state'
import componentState from '@/mixins/component-state'
import imageProxy from '@/libs/image-proxy'
import userAgent from '@/libs/user-agent'
import ModalProgress from '@/components/Modals/Progress'

import downloadAttachment from '@/mixins/download-attachment'
import ModalCustom from '@/components/Modals/Custom'
import features from '@/mixins/features'

export default {
  props: ['comment', 'selectedID', 'post', 'commentsLength', 'isGrid', 'selectedCommentID'],
  mixins: [auth, componentState, dropDownMixin, downloadAttachment, features],
  components: {
    HtmlView,
    ModalCustom,
    Carousel,
    Add,
    Report,
    DropDown,
    ModalProgress
  },
  data: function () {
    return {
      submitLike: false,
      reply: false,
      editing: false,
      saving: false,
      showEditModal: false,
      showDeleteModal: false,
      dropdowns: {
        likes: dropDownMixin.initDropdown()
      },
      fetchLimit: 10,
      canCreate: false,
      carouselPosition: 0,
      showDeleteAttachmentModal: false,
      projectRole: ''
    }
  },
  watch: {
    hasLiked: function (newHasLiked) {
      this.submitLike = newHasLiked
    }
  },
  created: function () {
    this.featureGroupName = 'Project View'
    this.getFeatures()
  },
  mounted: function () {
    if (this.isLoggedIn) {
      this.$store.dispatch('likedComment', this.likeParams)
      this.submitLike = this.hasLiked
    }
    if (this._get(this.updateKey)) {
      this.editing = true
    }

    if (this.$route.query.commentID && parseInt(this.$route.query.commentID) === this.comment.id && this.$route.query.reply) {
      this.$nextTick(() => {
        this.$emit('reply', this.comment)
      })
    }
    this.projectRole = this._get(state.project.role, 'none')
    this.configureLikeToolTip()
    this.fetchMembership()
  },
  computed: {
    isYSCAuditorium: function () {
      return this.$route.fullPath.includes('/auditoriumStory')
    },
    isTouch: function () {
      return userAgent.isIOS() || userAgent.isAndroid()
    },
    likeParams: function () {
      return { id: this.comment.id, username: this.currentUser.userName }
    },
    hasLiked: function () {
      return this.isLoggedIn ? this.$store.getters.likedComment(this.comment.id, this.currentUser.userName) : false
    },
    formattedDate: function () {
      let date = moment(this.comment.created)
      // no future comments
      if (date.isAfter(moment())) {
        date = moment()
      }
      if (date.isBefore(moment().subtract(2, 'days'))) {
        return date.format('MMM D, YYYY')
      } else {
        return date.fromNow()
      }
    },
    hasAttachments: function () {
      return this.comment.attachments.length > 0
    },
    attachments: function () {
      return this.comment.attachments
    },
    isCreator: function () {
      return this.isLoggedIn ? this.comment.user.uuid === this.currentUser.uuid : false
    },
    canEdit: function () {
      return this.canEditPost || this.isCreator
    },
    selected: function () {
      return this.comment.id === this.selectedID
    },
    updateKey: function () {
      return this._uicreateKey(this.post.projectID, this.comment, this.post, true)
    },
    canEditPost: function () {
      return this._get(state.posts.permissions.edit, false)
    },
    canCreateComment: function () {
      return this._get(state.comments.permissions.create, false)
    },
    uniqueClass: function () {
      return `like-button-${this._uid}`
    },
    stateActiveTab: function () {
      return state.activeTab
    },
    canEditComments: function () {
      return ['co-owner', 'anonymous-admin', 'admin', 'Teammate', 'Mentor', 'Teacher'].includes(this.projectRole)
    },
    isEditCommentPermission: function () {
      return this.hasFeature('EditComments') || this.canEditComments || this.canEdit
    },
    isDownloadable: function () {
      return this.hasAttachments && this.comment.attachments[this.carouselPosition].metadata?.isDownloadable
    },
    project: function () {
      return this._get(state.project.id) ? this.$store.getters.project(this._get(state.project.id)) || {} : {}
    },
    isSnapshot: function () {
      return this.project.type === 'snapshot'
    }
  },
  methods: {
    fetch: function (type, method) {
      const params = { id: this.comment.id, limit: this.fetchLimit, offset: this.dropdowns[type].offset }
      return this.fetchDropdown(type, method, params)
    },
    fetchLikedBy: function () {
      return this.fetch('likes', 'commentLikedBy')
    },
    showLikes: function () {
      return this.showDropdown('likes')
    },
    hideLikes: function (event) {
      return this.hideDropdown('likes', event)
    },
    toggleLikes: function () {
      this.submitLike = !this.hasLiked
      return this.toggleDropDown('likes')
    },
    edit: function () {
      this.editing = true
      // We have to wait for the element to become visible before we can get its position.
      this.$nextTick(() => {
        const commentId = this.comment.id
        const comment = document.getElementById(`comment-${commentId}`)
        comment.scrollIntoView()
      })
    },
    toggleEditModal: function () {
      this.showEditModal = !this.showEditModal
    },
    view: function (user) {
      this.$router.push({ name: 'profile', params: { username: user.userName } })
    },
    initiateCancel: function () {
      const newComment = this.$refs.add.$refs.createBox.body
      if (this.comment.body !== newComment) {
        this.toggleEditModal()
      } else {
        this.confirmCancel()
      }
    },
    confirmCancel: function () {
      this.showEditModal = false
      this.editing = false
      this.$refs.add.$refs.createBox.body = this.update ? this.comment.body : ''
      this._delete(this.updateKey)
    },
    thumbnail: imageProxy.thumbnail,

    like: throttle(function (some) {
      this.submitLike = true
      if (!this.isLoggedIn) {
        return this.$store.dispatch('showLoginModal', { source: 'commentlike:' + this.comment.projectID, onSuccess: this.like, signupMessage: 'Sign up to like ' + this.comment.user.userName + '\'s reply' })
      }

      if (this.hasLiked) {
        this.$store.dispatch('unlikeComment', this.likeParams).then(() => {
          this.configureLikeToolTip()
          this.resetDropdownUsers('likes')
          return this.fetch('likes', 'commentLikedBy')
        })
      } else {
        this.$store.dispatch('likeComment', this.likeParams).then(() => {
          this.configureLikeToolTip()
          this.resetDropdownUsers('likes')
          return this.fetch('likes', 'commentLikedBy')
        })
      }
    }, 500, { leading: true, trailing: false }),

    updateCarouselPosition: function (position) {
      this.carouselPosition = position
    },

    editAttachment: function () {
      this.edit()
      this.$nextTick(() => {
        this.editAttachmentInCarousel()
      })
    },

    editAttachmentInCarousel: function () {
      if (this.attachments[this.carouselPosition]) {
        this.$refs.add.$refs.createBox.$refs.attachments.editAttachment(this.carouselPosition)
      }
    },

    makeAnnotation: function (type, position) {
      if (!this.isLoggedIn) {
        return this.$store.dispatch('showLoginModal', { source: 'annotateComment:' + this.comment.projectID, onSuccess: this.makeAnnotation, signupMessage: 'Sign up to remix ' + this.comment.user.userName + '\'s whiteboard' })
      }
      const attachment = this.attachments[position]

      if (type === 'post') {
        this._set(`postFrom.${this.comment.projectID}`, attachment)
      } else {
        let replyTo = this.comment
        if (this.comment.parentID > 0) {
          replyTo = this.$store.state.comment.comments[this.comment.parentID]
        }

        this.$emit('reply', replyTo)
        this._set(`commentFrom.${this.comment.postID}`, attachment)
      }
    },

    deleteAttachment: function () {
      this.showDeleteAttachmentModal = false
      this.edit()
      this.$nextTick(() => {
        if (this.attachments[this.carouselPosition]) {
          this.$refs.add.$refs.createBox.$refs.attachments.deleteAttachment(this.carouselPosition)
        }
      })
    },

    formatTimestamp: function (timestamp) {
      const date = moment(timestamp)
      return date.format('YYYY-MM-DD, HH:mm:ss')
    },
    deleteComment: function () {
      const params = { postID: this.comment.postID, commentID: this.comment.id, parentID: this.comment.parentID }
      this.$store.dispatch('deleteComment', params).then((response) => {
      }, (error) => {
        this.error = error.message
        // i don't think the line below needs to be here
        this.loading = false
      })
    },
    configureLikeToolTip: function () {
      const likeButton = document.querySelector(`.${this.uniqueClass}`)
      if (likeButton === null) {
        return
      }
      if (this.comment.metadata.likes === 0) {
        likeButton.setAttribute('data-balloon', 'Like')
        likeButton.setAttribute('data-balloon-pos', 'up')
      } else {
        likeButton.removeAttribute('data-balloon')
        likeButton.removeAttribute('data-balloon-pos')
      }
    },
    fetchMembership: function () {
      if (!this.isLoggedIn || this.isGrid) {
        return false
      }
      this.$store.dispatch('getProject', { id: this.post.projectID, force: false }).then((project) => {
        return this.$store.dispatch('getMembership', { project, user: this.currentUser }).then((membership) => {
          this.canCreate = !!membership
        })
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~styles/utils.scss';

  .comment-box {
    background-color: #f2f2f2;
    position: relative;
    padding: 1.1rem 0 0.5rem 0;
    @include mobile {
      margin: 0.15rem 0.25rem 0;
      padding: 0.5rem 0;
    }
  }

  .card-container-comment {
    align-items: center;
    flex-wrap: wrap;
    position: relative;
    @include mobile{
      justify-content: initial;
    }
    .card-image-container {
      align-items: center;
      justify-content: flex-start;
      position: absolute;
      top: 0.5rem;
      .poster-image {
        width: 2.85rem;
        min-width: 2.85rem;
        height: 2.85rem;
        max-width: none;
        user-select: none;
        position: absolute;
        z-index: 3;
        @include mobile {
          width: 2.2rem;
          min-width: 2.2rem;
          height: 2.2rem;
        }
        img {
          border-radius: 50%;
          height: 100%;
          width: 100%;
          max-width: 100%;
          max-height: 100%;
        }
      }
    }
    .card-image-text{
      margin: 0 0 0.2rem 3.5rem;
      align-items: center;
      @include mobile {
        margin-left: 2.7rem;
      }
      .poster {
        font-size: 0.9rem;
        color: black;
        font-weight: bold;
        @include mobile {
          font-size: 0.8rem;
          font-weight: 800;
        }
      }
      .timestamp {
        font-size: 0.7rem;
        align-items: center;
        margin-left: 0.5rem;
        @include mobile {
          font-size: 0.8rem;
        }
      }
      .edit-comment {
        margin-left: 1rem;
        font-size: 0.65rem;
        color: #9e9e9e;
      }
    }
  }

.comment-text {
  background-color: #fff;
  border-radius: 10px;
  margin: 0.1rem 0.1rem 0 2.45rem;
  box-shadow: 0px 3px 10px -1px rgba(138,138,138,0.3);
  @include mobile {
    margin-left: 1.4rem;
    box-shadow: 0px 3px 10px -1px rgba(138,138,138,0.4);
  }
  .comment-carousel {
    margin: 0 0.6rem;
    padding-top: 0.6rem;
    border-top: 0;
    @include mobile {
      margin: 0 0.4rem;
      padding-top: 0.4rem;
    }
  }
  .text-and-like {
    flex-direction: column;
    height: 100%;
    flex-grow: 1;
    &.text-and-like-carousel {
      height: inherit;
    }
  }
  .post-text {
    flex-grow: 2;
    font-size: 0.95rem;
    line-height: 1.7;
    align-items: center;
    padding: 0 0.75rem;
    &-yscAuditorium {
      margin-bottom: -1rem;
    }
  }
  .button-container {
    margin: 0.5rem 0.5rem 0.75rem;
    justify-content: space-between;
    font-size: 0.8rem;
    .reply-and-like {
      align-items: center;
    }
    .reply, .thumb {
      font-size: 0.9rem;
      margin: 0 0.4rem;
      .far {
        color: $primary;
      }
      .dropper {
        position: relative;
      }
    }
    .thumb {
      line-height: 1rem;
      padding-right: 0.25rem;
      &:hover, .fa-thumbs-up.far {
        color: $primary;
      }
    }
    .reply:hover {
      color: $primary;
    }
    .active {
      color: $dark;
    }
    .edit-or-report span {
      margin: 0 0.5rem;
    }
    .edit-or-report:hover {
      color: $primary;
    }
  }
}

.isEng{
  background-color: #f8f8f8;
}

</style>
